import React, { useRef, useState, useEffect } from "react";
import { Send, Loader2, Sparkles, Compass } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const SUGGESTIONS = [
  "I'm feeling anxious today — any guidance?",
  "Share an encouraging thought for my recovery.",
  "How do I forgive someone who hurt me?",
  "Give me a short prayer for strength.",
];

const AskAI = () => {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [convId, setConvId] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const ask = async (text) => {
    const q = (text || draft).trim();
    if (!q) return;
    setDraft("");
    setMessages((m) => [...m, { role: "user", content: q }]);
    setLoading(true);
    try {
      const body = { question: q };
      if (convId) body.conversation_id = convId;
      const { data } = await api.post("/ai/ask", body);
      const answer = data.answer || data.response || data.message || data.reply || "";
      if (data.conversation_id) setConvId(data.conversation_id);
      setMessages((m) => [...m, { role: "ai", content: answer || "…" }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      setMessages((m) => [...m, {
        role: "ai",
        error: true,
        content: "Ask AI is temporarily unavailable. The guidance service is offline right now — please try again later.",
      }]);
      if (!String(detail).includes("inactive")) toast.error("Ask AI failed");
    } finally { setLoading(false); }
  };

  const submit = (e) => { e.preventDefault(); ask(); };

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col">
      <div className="max-w-3xl w-full mx-auto flex-1 flex flex-col px-4 sm:px-6">
        <div className="py-6">
          <div className="flex items-center gap-2 text-brass">
            <Sparkles className="w-5 h-5" />
            <h1 className="font-heading text-2xl font-semibold text-foreground">Ask AI Anytime</h1>
          </div>
          <p className="text-muted-foreground mt-1 text-sm">Faith-centered, recovery-minded guidance whenever you need it.</p>
        </div>

        <div className="flex-1 overflow-y-auto space-y-4 pb-4" data-testid="ask-ai-thread">
          {messages.length === 0 && !loading && (
            <div className="text-center py-10">
              <Compass className="w-10 h-10 text-primary/50 mx-auto mb-4" />
              <p className="text-muted-foreground mb-5">Ask anything — here are a few ideas:</p>
              <div className="flex flex-wrap justify-center gap-2">
                {SUGGESTIONS.map((s) => (
                  <button key={s} data-testid="ai-suggestion" onClick={() => ask(s)}
                    className="text-sm px-4 py-2 rounded-full border border-border hover:bg-secondary transition-colors">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[80%] px-4 py-3 leading-relaxed rounded-2xl ${
                m.role === "user" ? "bg-primary text-primary-foreground rounded-br-md"
                : m.error ? "bg-destructive/15 text-destructive border border-destructive/30"
                : "bg-secondary text-foreground rounded-bl-md"}`}>
                <p className="whitespace-pre-wrap">{m.content}</p>
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-secondary rounded-2xl rounded-bl-md px-4 py-3"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        <form onSubmit={submit} className="py-4 flex items-center gap-2">
          <input data-testid="ai-input" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Ask for guidance…"
            className="flex-1 px-4 py-3 rounded-full border border-input bg-secondary/40 outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-shadow" />
          <button data-testid="ai-send" type="submit" disabled={loading || !draft.trim()}
            className="w-12 h-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-atlantic active:scale-95 transition-all disabled:opacity-50 shrink-0">
            <Send className="w-5 h-5" />
          </button>
        </form>
      </div>
    </div>
  );
};

export default AskAI;
